import React from 'react';
import styled from 'styled-components';
import { FileMessageProps } from '../types/message';

const getFileUrl = (url: string, mimeType?: string) => {
  if (!url) return '';
  const addInlineIfNeeded = (value: string) => {
    if (!mimeType || !mimeType.startsWith('image/')) return value;
    if (value.includes('inline=')) return value;
    return `${value}${value.includes('?') ? '&' : '?'}inline=1`;
  };

  if (url.startsWith('/uploads/')) {
    const filename = url.split('/').pop();
    const base = (typeof window !== 'undefined' && window.supportlyChatConfig?.staticUrl)
      || process.env.REACT_APP_API_URL
      || window.location.origin;
    const fileUrl = base.replace(/\/$/, '') + '/api/messages/download/' + filename;
    return addInlineIfNeeded(fileUrl);
  }
  if (url.startsWith('/api/messages/download/') || url.includes('/api/messages/download/')) {
    const absolute = url.startsWith('http')
      ? url
      : `${((typeof window !== 'undefined' && window.supportlyChatConfig?.staticUrl) || process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
    return addInlineIfNeeded(absolute);
  }
  return url;
};

const FileContainer = styled.div<{ isUser: boolean }>`
  max-width: 260px;
  width: 100%;
  padding: 10px;
  border-radius: 15px;
  background: ${props => props.isUser ? 'rgba(0, 0, 0, 0.72)' : 'rgba(255, 255, 255, 0.14)'};
  color: #f1f1f1;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const FilePreview = styled.div`
  width: 100%;
  max-width: 200px;
  aspect-ratio: 4/3;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  overflow: hidden;
`;

const FileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FileName = styled.div`
  font-weight: 500;
  word-break: break-all;
`;

const FileSize = styled.div`
  font-size: 0.8em;
  opacity: 0.8;
`;

const FileActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const FileButton = styled.a`
  padding: 4px 8px;
  background: ${props => props.color || 'rgba(255, 255, 255, 0.2)'};
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.9em;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const FileMessage: React.FC<FileMessageProps> = ({ file, timestamp, isUser }) => {
  console.log('[FileMessage] received props:', { file, timestamp, isUser });
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImage = file.type.startsWith('image/');
  const isPDF = file.type === 'application/pdf';

  return (
    <FileContainer isUser={isUser}>
      {isImage && (
        <FilePreview>
          <FileImage src={getFileUrl(file.url, file.type)} alt={file.name} />
        </FilePreview>
      )}
      <FileInfo>
        <FileName>{file.name}</FileName>
        <FileSize>{formatFileSize(file.size)}</FileSize>
        <FileActions>
          <FileButton href={getFileUrl(file.url, file.type)} download={file.name} color="rgba(255, 255, 255, 0.22)">
            Download
          </FileButton>
          {isPDF && (
            <FileButton href={getFileUrl(file.url)} target="_blank" color="rgba(255, 255, 255, 0.32)">
              View
            </FileButton>
          )}
        </FileActions>
      </FileInfo>
    </FileContainer>
  );
};

export default FileMessage; 
